// Carte interactive du PCG : grille des comptes 2 chiffres regroupés par grandes catégories
// (Bilan / Gestion / Spéciaux), avec cellules cliquables menant à la fiche correspondante.

const GROUPS = [
 {
 label:"Comptes de bilan",
 sublabel:"Classes 1 à 5",
 color:"#15294F",
 bg:"#EBF1FB",
 classes: [1, 2, 3, 4, 5],
 },
 {
 label:"Comptes de gestion",
 sublabel:"Compte de résultat – Classes 6 et 7",
 color:"#A33B4B",
 bg:"#FBF1F1",
 classes: [6, 7],
 },
 {
 label:"Comptes spéciaux",
 sublabel:"Classe 8",
 color:"#1F4D3A",
 bg:"#F0F5F0",
 classes: [8],
 },
];

const CLASS_LABELS = {
 1:"Comptes de capitaux",
 2:"Comptes d'immobilisations",
 3:"Comptes de stocks et en-cours",
 4:"Comptes de tiers",
 5:"Comptes financiers",
 6:"Comptes de charges",
 7:"Comptes de produits",
 8:"Comptes spéciaux",
};

// Libellés des comptes à 2 chiffres tels qu'organisés dans le tableau officiel
const CELLS = {
 10:"Capital et réserves",
 11:"Report à nouveau",
 12:"Résultat de l'exercice",
 13:"Subventions d'investissement",
 14:"Provisions réglementées",
 15:"Provisions",
 16:"Emprunts et dettes assimilées",
 17:"Dettes rattachées à des participations",
 18:"Comptes de liaison des établissements",
 19: null,
 20:"Immo incorporelles et frais d'établissement",
 21:"Immobilisations corporelles",
 22:"Immobilisations mises en concession",
 23:"Immobilisations en cours, avances",
 24: null,
 25: null,
 26:"Participations et créances rattachées",
 27:"Autres immobilisations financières",
 28:"Amortissements des immobilisations",
 29:"Dépréciations des immobilisations",
 30: null,
 31:"Matières premières et fournitures",
 32:"Autres approvisionnements",
 33:"En-cours de production de biens",
 34:"En-cours de production de services",
 35:"Stocks de produits",
 36:"Stocks d'immobilisations (si nécessaire)",
 37:"Stocks de marchandises",
 38:"Stocks en voie d'acheminement",
 39:"Dépréciations des stocks et en-cours",
 40:"Fournisseurs et comptes rattachés",
 41:"Clients et comptes rattachés",
 42:"Personnel et comptes rattachés",
 43:"Sécurité sociale et autres organismes",
 44:"État et autres collectivités publiques",
 45:"Groupe et associés",
 46:"Débiteurs divers et créditeurs divers",
 47:"Comptes transitoires ou d'attente",
 48:"Comptes de régularisation",
 49:"Dépréciations des comptes de tiers",
 50:"Valeurs mobilières de placement",
 51:"Banques, établissements financiers",
 52:"Instruments financiers à terme",
 53:"Caisse",
 54: null,
 55: null,
 56: null,
 57: null,
 58:"Virements internes",
 59:"Dépréciations des comptes financiers",
 60:"Achats (sauf 603)",
 61:"Services extérieurs",
 62:"Autres services extérieurs",
 63:"Impôts, taxes et versements assimilés",
 64:"Charges de personnel",
 65:"Autres charges de gestion courante",
 66:"Charges financières",
 67:"Charges exceptionnelles",
 68:"Dotations aux amortissements et provisions",
 69:"Participation, impôts sur les bénéfices",
 70:"Ventes de produits, prestations, marchandises",
 71:"Production stockée (ou déstockage)",
 72:"Production immobilisée",
 73: null,
 74:"Subventions d'exploitation",
 75:"Autres produits de gestion courante",
 76:"Produits financiers",
 77:"Produits exceptionnels",
 78:"Reprises sur amortissements et provisions",
 79: null,
};

export default function PCGMap({ onSelect }) {
 // Build a 10×8 grid using CSS Grid with explicit row/col positioning
 return (
 <div data-testid="pcg-map" className="space-y-4">
 <div className="flex items-baseline justify-between gap-3">
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] tracking-tight">
 Carte interactive du PCG
 </h2>
 <span className="md:hidden text-[10px] uppercase tracking-wider text-slate-400 font-semibold shrink-0">
 ← défiler →
 </span>
 </div>

 {/* Wrapper scroll horizontal sur mobile : la grille 8 colonnes reste lisible */}
 <div className="-mx-3 px-3 md:mx-0 md:px-0 overflow-x-auto pcg-map-scroll">
 <div className="min-w-[760px] md:min-w-0 space-y-1.5 md:space-y-2">

 {/* Headers : 3 groupes au-dessus de 8 colonnes */}
 <div
 className="grid gap-1 md:gap-2"
 style={{ gridTemplateColumns:"repeat(8, minmax(0, 1fr))" }}
 >
 {GROUPS.map((g) => (
 <div
 key={g.label}
 className="text-center py-3 px-2 rounded-md border"
 style={{
 gridColumn: `span ${g.classes.length} / span ${g.classes.length}`,
 backgroundColor: g.bg,
 borderColor: g.color +"33",
 }}
 >
 <div
 className="font-heading font-extrabold text-xs md:text-sm uppercase tracking-wider"
 style={{ color: g.color }}
 >
 {g.label}
 </div>
 <div className="text-[10px] text-slate-500 mt-0.5 hidden md:block">
 {g.sublabel}
 </div>
 </div>
 ))}
 </div>

 {/* Entêtes des 8 classes */}
 <div
 className="grid gap-1 md:gap-2"
 style={{ gridTemplateColumns:"repeat(8, minmax(0, 1fr))" }}
 >
 {[1, 2, 3, 4, 5, 6, 7, 8].map((cls) => {
 const group = GROUPS.find((g) => g.classes.includes(cls));
 return (
 <button
 key={cls}
 data-testid={`pcg-map-class-${cls}`}
 onClick={() => onSelect(String(cls))}
 className="text-left py-2 px-2 rounded-md border hover:shadow-keyto transition-all"
 style={{ backgroundColor: group.color, borderColor: group.color }}
 >
 <div className="font-mono font-black text-white text-base">{cls}</div>
 <div className="font-heading font-bold text-[10px] md:text-xs text-white/90 leading-tight mt-0.5">
 {CLASS_LABELS[cls]}
 </div>
 </button>
 );
 })}
 </div>

 {/* Grille principale : 8 colonnes × 10 lignes, positionnement explicite */}
 <div
 className="grid gap-1 md:gap-2"
 style={{
 gridTemplateColumns:"repeat(8, minmax(0, 1fr))",
 gridTemplateRows:"repeat(10, minmax(64px, auto))",
 }}
 >
 {[1, 2, 3, 4, 5, 6, 7].map((cls) =>
 Array.from({ length: 10 }, (_, row) => {
 const num = `${cls}${row}`;
 const label = CELLS[parseInt(num, 10)];
 const group = GROUPS.find((g) => g.classes.includes(cls));
 const style = { gridColumn: cls, gridRow: row + 1 };
 if (!label) {
 return (
 <div
 key={num}
 style={style}
 className="rounded-md border border-dashed border-slate-200 bg-slate-50/50 p-2 md:p-3 flex items-start"
 >
 <span className="font-mono font-bold text-xs text-slate-300">{num}</span>
 </div>
 );
 }
 return (
 <button
 key={num}
 data-testid={`pcg-map-cell-${num}`}
 onClick={() => onSelect(num)}
 style={{
 ...style,
 borderColor: group.color +"33",
 borderLeftWidth: 3,
 borderLeftColor: group.color,
 }}
 className="rounded-md border bg-white p-2 md:p-3 flex flex-col items-start text-left hover:shadow-keyto hover:-translate-y-0.5 transition-all group"
 >
 <span className="font-mono font-bold text-xs" style={{ color: group.color }}>
 {num}
 </span>
 <span className="text-[10px] md:text-[11px] text-slate-700 leading-tight mt-1 font-medium group-hover:text-[#15294F]">
 {label}
 </span>
 </button>
 );
 })
 )}

 {/* Colonne 8 (Comptes spéciaux) — span 10 lignes */}
 <button
 data-testid="pcg-map-cell-8"
 onClick={() => onSelect("8")}
 style={{
 gridColumn: 8,
 gridRow:"1 / span 10",
 backgroundColor:"#F0F5F0",
 borderColor:"#1F4D3A33",
 }}
 className="rounded-md border p-3 text-center hover:shadow-keyto transition-all flex flex-col items-center justify-center"
 >
 <div className="font-heading font-extrabold text-xs text-[#1F4D3A] uppercase tracking-wider leading-tight">
 Comptes spéciaux
 </div>
 <div className="text-[10px] text-slate-600 mt-3 leading-snug px-1">
 Comptes hors bilan et engagements (cautions, garanties, etc.) qui n'ont pas leur place
 dans les classes 1 à 7.
 </div>
 <div className="font-mono font-black text-4xl text-[#1F4D3A] mt-4">8</div>
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}
